/** Maps the old librarian dashboard URLs onto the current routes, carrying query parameters across. */

export interface LegacyRouteMapping {
  pattern: RegExp;
  newRoute: string;
  tabParam?: string;
  paramMappings?: Record<string, string>;
  description: string;
}

/**
 * Legacy route mappings for automatic redirection
 */
export const legacyRouteMappings: LegacyRouteMapping[] = [
  {
    pattern: /^\/librarian\/book-management/,
    newRoute: '/librarian/books',
    tabParam: 'catalog',
    description: 'Book Management Hub → Catalog Tab'
  },
  {
    pattern: /^\/librarian\/issue/,
    newRoute: '/librarian/books',
    tabParam: 'issue-return',
    paramMappings: {
      'bookId': 'selectedBook',
      'memberId': 'selectedMember',
      'action': 'workflow'
    },
    description: 'Book Issue Page → Issue & Return Tab'
  },
  {
    pattern: /^\/librarian\/return/,
    newRoute: '/librarian/books',
    tabParam: 'issue-return',
    paramMappings: {
      'bookId': 'selectedBook',
      'memberId': 'selectedMember',
      'action': 'workflow'
    },
    description: 'Book Return Page → Issue & Return Tab'
  },
  {
    pattern: /^\/librarian\/inventory/,
    newRoute: '/librarian/books',
    tabParam: 'inventory',
    paramMappings: {
      'bookId': 'selectedBook',
      'locationId': 'selectedLocation'
    },
    description: 'Inventory Page → Inventory Tab'
  },
  {
    pattern: /^\/librarian\/bulk-operations/,
    newRoute: '/librarian/books',
    tabParam: 'bulk-operations',
    description: 'Bulk Operations → Bulk Operations Tab'
  },
  {
    pattern: /^\/librarian\/analytics/,
    newRoute: '/librarian/books',
    tabParam: 'analytics',
    description: 'Analytics → Analytics Tab'
  }
];

/** The mapping for a legacy URL, or null. */
export const findNewRoute = (oldPath: string): LegacyRouteMapping | null => {
  return legacyRouteMappings.find(mapping => mapping.pattern.test(oldPath)) || null;
};

/**
 * Build a new URL with preserved and mapped parameters
 */
export const buildMigratedUrl = (
  mapping: LegacyRouteMapping,
  searchParams: URLSearchParams
): string => {
  const newParams = new URLSearchParams();
  
  // Add the tab parameter if specified
  if (mapping.tabParam) {
    newParams.set('tab', mapping.tabParam);
  }
  
  // Apply parameter mappings and preserve others
  searchParams.forEach((value, key) => {
    if (mapping.paramMappings && mapping.paramMappings[key]) {
      // Use mapped parameter name
      newParams.set(mapping.paramMappings[key], value);
    } else if (key !== 'tab' || !mapping.tabParam) {
      // Preserve original parameter if not conflicting with tab
      newParams.set(key, value);
    }
  });
  
  const queryString = newParams.toString();
  return queryString ? `${mapping.newRoute}?${queryString}` : mapping.newRoute;
};

/**
 * Check if a URL needs migration
 */
export const needsMigration = (path: string): boolean => {
  return legacyRouteMappings.some(mapping => mapping.pattern.test(path));
};

/**
 * Get migration information for a specific path
 */
export const getMigrationInfo = (oldPath: string) => {
  const mapping = findNewRoute(oldPath);
  if (!mapping) return null;
  
  return {
    from: oldPath,
    to: mapping.newRoute,
    tab: mapping.tabParam,
    description: mapping.description,
    hasParameterMappings: !!mapping.paramMappings
  };
};

/** The toast text for a redirect. */
export const generateMigrationMessage = (oldPath: string, newPath: string): string => {
  const mapping = findNewRoute(oldPath);
  if (!mapping) {
    return `This page has moved to ${newPath}`;
  }
  
  const messages = {
    'catalog': 'Book management is now the Catalog tab',
    'issue-return': 'Issuing and returning are now the Issue & Return tab',
    'inventory': 'Inventory is now the Inventory tab',
    'bulk-operations': 'Bulk operations are now the Bulk Operations tab',
    'analytics': 'Analytics and reports are now the Analytics tab'
  };
  
  return mapping.tabParam && messages[mapping.tabParam as keyof typeof messages]
    ? messages[mapping.tabParam as keyof typeof messages]
    : mapping.description;
};

/** Record a redirect. */
export const trackMigration = (oldPath: string, newPath: string) => {
  // Console and localStorage only; nothing reports these anywhere.
  console.log(`Route migration: ${oldPath} → ${newPath}`);
  
  // Store in localStorage for user feedback
  const migrations = JSON.parse(localStorage.getItem('route-migrations') || '[]');
  migrations.push({
    from: oldPath,
    to: newPath,
    timestamp: new Date().toISOString()
  });
  
  // Keep only last 10 migrations
  if (migrations.length > 10) {
    migrations.splice(0, migrations.length - 10);
  }
  
  localStorage.setItem('route-migrations', JSON.stringify(migrations));
};

/**
 * Get recent migrations for user reference
 */
export const getRecentMigrations = () => {
  return JSON.parse(localStorage.getItem('route-migrations') || '[]');
};

/**
 * Clear migration history
 */
export const clearMigrationHistory = () => {
  localStorage.removeItem('route-migrations');
};